/* Global idChercheurActif, globalIdProjet, autoSave */

/**
 * @file JS - Controller - ProjectController.js
 * @author : Adam Lemire
 * @since 2019/06/13
 * @version 1.0
 * @summary Dernières modifications : 2022/05/03
 */

import SingleMapPageController from './Page/SingleMapPageController.js';
import MultiMapPageController from './Page/MultiMapPageController.js';
import { gisSettings } from './../gisSettings.js';
import Project from '../classes/Project.js';
import { GisSection, ContextMenu, LayoutSection, MapTabsContextMenuOptions, MapRenameForm, ProjectMenu, FormulaireOuvrirProjetChercheur } from './../helpers/Selectors.js';
import { dictionary } from './../helpers/languageDictionary.js';
import { hideContextMenus, isJson } from './../helpers/GlobalFunctions.js';
import { MultiMapPageFormat } from '../helpers/Constants.js'; // eslint-disable-line no-unused-vars
import { Evenements } from '../events/Evenements.js';
import SingleMapPage from '../classes/Page/SingleMapPage.js';
import MultiMapPage from '../classes/Page/MultiMapPage.js'; // eslint-disable-line no-unused-vars
import DataPage from '../classes/Page/DataPage.js'; // eslint-disable-line no-unused-vars
import LeftMenuController from './LeftMenuController';
import { PageType } from '../classes/Page/Page.js';
require('jquery-ui/ui/core');
require('jquery-ui/ui/effects/effect-shake');

const HTML_AVERTISSEMENT = '.appli-sig__modale-avertissement';
const HTML_AVERTISSEMENT_MESSAGE = '.modale-avertissement__message';
const HTML_AVERTISSEMENT_FERMER = '.modale-avertissement__fermer';

const HTML_MENUGAUCHE_BTNS_MULTIMAP = '.left-menu__button--multimap';
const HTML_MENUGAUCHE_BTNS_SINGLEMAP = '.left-menu__button--singlemap';
// const globalIdProjet = '83';

const OPTION_CHARGER_PROJET_LOCAL = gisSettings.options.project.chargementLocal.active;
const OPTION_CHARGER_PROJET_API = gisSettings.options.project.chargementDepuisEspaceChercheur.active;
const OPTION_EDITER_NOM_PROJET = gisSettings.options.project.editName.active;
const OPTION_EDITER_NOM_CARTE = gisSettings.options.map.editName.active;
const OPTION_SUPPRIMER_CARTE = gisSettings.options.map.delete.active;
const OPTION_ONGLETS_CARTES = gisSettings.mapTabsBar.active;
const OPTION_MENU_PROJET = gisSettings.topMenu.projectMenu.active;
const OPTION_MENU_HAUT = gisSettings.topMenu.active;
const OPTION_BARRE_TITRE = gisSettings.mapTitleBar.active;
const OPTION_AUTOSAVE_API = gisSettings.options.project.autoSave.active;
const OPTION_URL_API_PROJETS = gisSettings.url.api.listeProjetsEspaceChercheur;
const OPTION_URL_API_OUVRIR_PROJET = gisSettings.url.api.ouvertureProjetEspaceChercheur;

const URL_BASE = window.location.pathname;

/**
 * @classdesc Controller d'objet Project - fait la relation entre le Project et les éléments qui l'utilise
 */
export default class ProjectController {
  /**
   * @example
   * proj = new ProjectController(version);
   * proj.activate();
   * @param { string } version - version du sig actuel
   * @param { string } projectJson - json du projet si ouverture d'un projet existant
   */
  constructor (version, projectJson) {
    this._projet = new Project({
      title: window.localStorage.getItem('lang') === 'en' ? dictionary.defaultText.projectTitle.en : dictionary.defaultText.projectTitle.fr,
      gisVersion: version
    });
    this._controleurPageCarteUnique = new SingleMapPageController();
    this._controleurPageCartesMultiples = new MultiMapPageController();
    this._gisVersion = version;
    this._langueAffichageChangeeListeners = [];
  }

  get projet () { return this._projet; }

  /*
   * GENERAL
   */

  async activer () {
    const _this = this;
    this._inscrireAuditeurs();

    if (OPTION_AUTOSAVE_API && typeof globalIdProjet !== 'undefined') {
      console.log('ProjectController: activer projet: ' + globalIdProjet);
      const doitCréerNouveauProjet = await this.verifierSiDoitCreerNouveauProjetSelonAPI();

      if (!doitCréerNouveauProjet) {
        console.log('ProjectController: _gererOuvertureProjetAPISurChargement');
        this._gererOuvertureProjetAPISurChargement(globalIdProjet);
      } else {
        this._inscrireAuditeursDépendantsDeProjet(this._projet);
        this.ajouterPageCarteUnique();
        this._controleurPageCartesMultiples.activer();
        Evenements.doitEnregistrer.subscribe(_this.exporterProjetVersAPI.bind(_this, true));
      }
      console.log('ProjectController: doit tester si multimappage fonctionne suivant cet appel');
      // @todo doit tester si multimappage fonctionne suivant cet appel
    } else {
      this._inscrireAuditeursDépendantsDeProjet(this._projet);
      this.ajouterPageCarteUnique();
      this._controleurPageCartesMultiples.activer();
      if (OPTION_AUTOSAVE_API) { Evenements.doitEnregistrer.subscribe(_this.exporterProjetVersAPI.bind(_this, true)); }
    }
    this._gérerURLRefresh();
  }

  async verifierSiDoitCreerNouveauProjetSelonAPI () {
    const jsonProjetAPI = await obtenirProjetJsonEspaceChercheur(globalIdProjet);
    if (jsonProjetAPI && (jsonProjetAPI.creationDate || jsonProjetAPI.saveDate)) {
      console.log('ProjectController: ne pas créer nouveau projet');
      return false;
    } else {
      console.log('ProjectController: créer nouveau projet');
      return true;
    }
  }

  // @todo mettre texte en constantes importées
  _gérerURLRefresh () {
    const urlActuelle = window.location.href;
    const nextURL = urlActuelle.replace('&save', '');
    const urlSansProjet0 = nextURL.replace('?projet=0', '');
    const nextTitle = 'i-Balsac';
    const nextState = { additionalInformation: 'Updated the URL with JS' };

    // This will create a new entry in the browser's history, without reloading
    window.history.pushState(nextState, nextTitle, urlSansProjet0);

    // This will replace the current entry in the browser's history, without reloading
    window.history.replaceState(nextState, nextTitle, urlSansProjet0);
  }

  /*
   * Auditeurs
   */

  _inscrireAuditeurs () {
    if (OPTION_MENU_PROJET) {
      this._ecouterBoutonsMenuProjet();
      if (OPTION_CHARGER_PROJET_LOCAL) this._ecouterBoutonsFormulaireOuvrirProjet();
      if (OPTION_CHARGER_PROJET_API) this._inscrireAuditeursOuvrirProjetDepuisEspaceCheurcheur();
    }
    if (OPTION_EDITER_NOM_PROJET) {
      écouterDeclencheursAfficherRenommerProjet();
      this._ecouterBoutonsFormulaireRenommerProjet();
    }
    if (OPTION_EDITER_NOM_CARTE) this._ecouterFormulaireRenommerCarte();
    this._ecouterChangementPageCarteActive();
    this._ecouterAjoutPageCarteUnique();
    this._ecouterSuppressionPageCarteUnique();
    this._écouterAjoutPageCartesMultiples();
    this._inscrireAuditeursDataPage();
    this._inscrireAuditeursDépendantsDeProjet(this._projet);
    écouterModaleAvertissement();
  }

  /** @param { Project } projet */
  _inscrireAuditeursDépendantsDeProjet (projet) {
    this._ecouterOptionsMenusContextuels(this._projet);
  }

  _ecouterBoutonsMenuProjet () {
    // ProjectMenu.BOUTON_EXPORTER dans GisController.js
    $(ProjectMenu.BUTTONS).off('click tap').on('click tap', () => $(ContextMenu.PROJECT_MENU).toggleClass('hidden'));
    $(ProjectMenu.OPEN_BUTTON).off('click tap').on('click tap', () => $(ProjectMenu.OPEN_PROJECT_FORM.SECTION).toggleClass('hidden'));
    if (OPTION_EDITER_NOM_PROJET) {
      $(ProjectMenu.RENAME_BUTTON).off('click tap').on('click tap', () => afficherFormulaireRenommerProjet());
    }
  }

  _ecouterBoutonsFormulaireRenommerProjet () {
    const _this = this;
    $(ProjectMenu.RENAME_FORM.CANCEL).off('click tap').on('click tap', () => cacherFormulaireRenommerProjet());
    $(ProjectMenu.RENAME_FORM.APPLY).off('click tap').on('click tap', () => _this._renommerProjet());
    $(ProjectMenu.RENAME_FORM.INPUT).off('keyup').on('keyup', (e) => _this._renommerProjetSurToucheEntrer(e));
  }

  /*
   * ouvrir projet local
   */
  _ecouterBoutonsFormulaireOuvrirProjet () {
    const _this = this;
    $(ProjectMenu.OPEN_PROJECT_FORM.INPUT).off('change').on('change', (event) => _this._ecouterBoutonOuvrirProjetLocalSelectionne(event));
    $(ProjectMenu.OPEN_PROJECT_FORM.OPEN).off('click tap').on('click tap', () => $(ProjectMenu.OPEN_PROJECT_FORM.NO_PROJECT_TEXT).effect('shake'));
    $(ProjectMenu.OPEN_PROJECT_FORM.CANCEL).off('click tap').on('click tap', () => fermerFormulaireOuvrirProjetLocal());
  }

  /**
   * @description fichier provenant du client
   * @param { event } evt
   */
  _ecouterBoutonOuvrirProjetLocalSelectionne (evt) {
    const _this = this;
    const file = evt.target.files[0]; // FileList object

    if (file !== 'undefined') {
      $(ProjectMenu.OPEN_PROJECT_FORM.NO_PROJECT).hide();
      $(ProjectMenu.OPEN_PROJECT_FORM.FILE_NAME).show().text(evt.target.files[0].name);
      $(ProjectMenu.OPEN_PROJECT_FORM.OPEN).off('click tap').on('click tap', () => _this._chargerProjetDepuisFichierLocal(file));
    } else {
      $(ProjectMenu.OPEN_PROJECT_FORM.NO_PROJECT).show();
      $(ProjectMenu.OPEN_PROJECT_FORM.FILE_NAME).hide().text('');
      $(ProjectMenu.OPEN_PROJECT_FORM.OPEN).off('click tap').on('click tap', () => $(ProjectMenu.OPEN_PROJECT_FORM.NO_PROJECT_TEXT).effect('shake'));
    }
  }

  _ecouterFormulaireRenommerCarte () {
    const _this = this;
    $(MapRenameForm.CANCEL).off('click tap').on('click tap', () => cacherFormulaireRenommerPageCarte());
    $(MapRenameForm.APPLY).off('click tap').on('click tap', () => _this._renommerPageCarte());
    $(MapRenameForm.INPUT).off('keyup').on('keyup', (e) => _this._renommerPageCarteSurToucheEntrer(e));
  }

  /** @param { Project } projet */
  _ecouterOptionsMenusContextuels (projet) {
    if (OPTION_ONGLETS_CARTES) {
      if (OPTION_EDITER_NOM_CARTE) $(MapTabsContextMenuOptions.RENAME).off('click tap').on('click tap', () => afficherFormulaireRenommerPageCarte());
      if (OPTION_SUPPRIMER_CARTE) $(MapTabsContextMenuOptions.DELETE).off('click tap').on('click tap', () => supprimerPageCarteDeProjet(projet));
    }
    if (OPTION_BARRE_TITRE && OPTION_EDITER_NOM_PROJET) {
      $(ContextMenu.HEADER.RENAME).off('click tap').on('click tap', () => afficherFormulaireRenommerProjet());
    }
  }

  _ecouterChangementPageCarteActive () {
    const _this = this;
    this._projet.registerActiveMapPageListener(function (mapPage) {
      afficherPageCarte(mapPage);
      $(GisSection.DATA).hide();
      $('.data__section').hide();
      mapPage instanceof SingleMapPage
        ? _this._controleurPageCarteUnique.pageCarteUnique = mapPage
        : activerPageCartesMultiples(mapPage, _this._controleurPageCartesMultiples);
      actualiserTexteEntete(_this._projet);
      écouterMenuContextuelEntête();
    });
  }

  /*
   * IMPORTER PROJET DEPUIS ESPACE CHERCHEUR
   */

  /**
   * @param { string } idProjet
   * @description Fonctionnel 2023/01/31 //! sauf si activeMapPage est MutliMap, affiche parfois libellés même si doivent être cachés
   */
  async _gererOuvertureProjetAPISurChargement (idProjet) {
    const _this = this;
    const projetJSON = await obtenirProjetJsonEspaceChercheur(idProjet, autoSave); // eslint-disable-line
    const projet = await this._convertirProjetJSONVersProjet(await projetJSON);

    enleverAncienProjetDansVue();
    this._projet = await projet;
    this._activerProjetDepuisJSON(projetJSON, await this._projet);
    this._controleurPageCarteUnique.activerEssentiel(await this._projet);
    // afficherListeCouchesSiPageActiveEstPageCarteUnique(await this._projet);
    // LeftMenuController.afficherCouchesDeCarteActive(await this._projet);
    Evenements.doitEnregistrer.subscribe(_this.exporterProjetVersAPI.bind(_this, true));

    // function afficherListeCouchesSiPageActiveEstPageCarteUnique (proj) {
    //   if (projet.activeMapPage.pageType === PageType.SINGLEMAPPAGE) {
    //     LeftMenuController.afficherCouchesDeCarteActive();
    //   }
    // }
  }

  _inscrireAuditeursOuvrirProjetDepuisEspaceCheurcheur () {
    if (OPTION_MENU_HAUT && OPTION_MENU_PROJET) {
      $(ProjectMenu.OUVRIR_PROJET_CHERCHEUR_BUTTON).off('click tap').on('click tap', () => chargerFormulaireOuvrirProjetEspaceChercheur());
    }
    this._ecouterBoutonsFormulaireOuvrirProjetEspaceCheurcheur();
  }

  _ecouterBoutonsFormulaireOuvrirProjetEspaceCheurcheur () {
    const _this = this;
    $(FormulaireOuvrirProjetChercheur.CANCEL_BUTTON).off('click tap').on('click tap', () => changerVisibiliteFormulaireOuvrirProjetEspaceChercheur());
    $(FormulaireOuvrirProjetChercheur.OPEN_BUTTON).off('click tap').on('click tap', () => _this._gererOuvertureProjetEspaceChercheur());
  }

  // @todo vérifier si problème avec DataPage
  async _gererOuvertureProjetEspaceChercheur () {
    const idProjet = $('.researcher-project-list input:checked').parent().data('id-projet');
    console.log('ProjectController: idProjet : %o', idProjet);

    if (!idProjet) {
      $(FormulaireOuvrirProjetChercheur.OPEN_BUTTON).effect('shake');
    } else {
      const fichierJSON = await obtenirProjetJsonEspaceChercheur(idProjet);
      console.log('ProjectController: ICI, vérifier si problème avec DataPage');
      this._ouvrirProjetDepuisAPI(fichierJSON);
      changerVisibiliteFormulaireOuvrirProjetEspaceChercheur();
    }
  }

  /** @param { JSON } jsonFile */
  async _ouvrirProjetDepuisAPI (jsonFile) {
    const dataJSON = jsonFile;
    console.log('ProjectController: dataJSON :%o', dataJSON);

    this._ouvrirProjetDepuisFichierJson(dataJSON);
  }

  /*
   * DataPage
   */

  _inscrireAuditeursDataPage () {
    const _this = this;
    Evenements.dataPageAjoutee.subscribe(_this._gererDataPageAjoutee.bind(_this));
    Evenements.dataPageSupprimee.subscribe(_this._gererDataPageSupprimee.bind(_this));
  }

  /** @param { DataPage } dataPage */
  _gererDataPageAjoutee (dataPage) {
    const _this = this;
    Evenements.langueAffichageChangee.subscribe(_this._changerLangueDataPage.bind(_this, dataPage));
  }

  /** @param { DataPage } dataPage */
  _gererDataPageSupprimee (dataPage) {
    const _this = this;
    Evenements.langueAffichageChangee.unsubscribe(_this._changerLangueDataPage.bind(_this, dataPage));
  }

  _changerLangueDataPage (dataPage) {
    this._controleurPageCarteUnique.changerLangueDataPage(dataPage);
  }

  /*
   * Fonctions des listeners
   */

  _renommerProjet () {
    const newName = $(ProjectMenu.RENAME_FORM.INPUT).val();
    if (newName.length > 0) {
      this._projet.title = newName;
      actualiserTexteEntete(this._projet);
      cacherFormulaireRenommerProjet();
    } else {
      $(ProjectMenu.RENAME_FORM.INPUT).effect('shake', 500);
    }
  }

  _renommerProjetSurToucheEntrer (event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      this._renommerProjet();
    }
  }

  /*
   * AJOUTER PAGE CARTE UNIQUE
   */

  /** @param { string } nomPageExistant - Nom de la page (carte) */
  ajouterPageCarteUnique (nomPageExistant) {
    this._projet.pageCount++;
    const nomPage = nomPageExistant || window.localStorage.getItem('lang') === 'en' ? dictionary.defaultText.singleMapName.en + ' ' + this._projet.pageCount : dictionary.defaultText.singleMapName.fr + ' ' + this._projet.pageCount;

    this._ajouterPageCarteUniqueDansVue(nomPage); // la div de la map doit exister avant d'instancier la carte openlayer dans le model
    this._projet.addSingleMapPageToModel(nomPage);
    this._controleurPageCarteUnique.activer();
    // console.log('Test activerNouvellePage()');
    // this._projet.pageCount === 1 ? this._controleurPageCarteUnique.activer() : this._controleurPageCarteUnique.activerNouvellePage();
  }

  /**
   * @param { string } pageName - Nom de la page (carte)
   * @param { number } jsonMapPageId - Optionel
   */
  _ajouterPageCarteUniqueDansVue (pageName, jsonMapPageId) {
    const idPageCarte = jsonMapPageId || this._projet.pageCount;
    SingleMapPageController.ajouterSectionPageCarteUniqueDansVue(idPageCarte);
    if (OPTION_ONGLETS_CARTES) this._ajouterOngletPageCarteDansVue(pageName, idPageCarte);
  }

  /** @param { number } pageId */
  _ecouterClicOngletPageCarte (pageId) {
    const _this = this;
    const tab = '.tab__map-' + pageId;
    $(tab).off('click tap').on('click tap', () => _this._projet.setActiveMapPage(pageId));
  }

  _ecouterAjoutPageCarteUnique () {
    const _this = this;
    this._projet.registerSingleMapPageAddedListener(function (singleMapPage) {
      _this._controleurPageCartesMultiples.addSingleMapPage(singleMapPage);
      Evenements.doitEnregistrer.notify(singleMapPage);
    });
  }

  _ecouterSuppressionPageCarteUnique () {
    const _this = this;
    this._projet.registerSingleMapPageRemovedListener(function (singleMapPage) {
      _this._controleurPageCartesMultiples.removeSingleMapPage(singleMapPage);
      Evenements.doitEnregistrer.notify(singleMapPage);
    });
  }

  /**
   * @param { string } name - Nom de la page (carte)
   * @param { number } pageId
   */
  _ajouterOngletPageCarteDansVue (name, pageId) {
    const newTab = '<div title="tab__' + pageId + '" id="tab__map-' + pageId + '" class="map-tabs__tab tab__map-' + pageId + '">' + name + '</div>';
    $(GisSection.MAP_TABS.SECTION).append(newTab);
    this._ecouterClicOngletPageCarte(pageId);
    écouterMenuContextuelOngletPageCarte(pageId);
  }

  /*
   * AJOUTER PAGE CARTES MULTIPLES
   */

  /**
   * @param { MultiMapPageFormat } format
   * @param { string } pageName - Nom de la page (carte)
   */
  ajouterPageCartesMultiples (format, pageName = window.localStorage.getItem('lang') === 'en' ? dictionary.defaultText.multiMapName.en : dictionary.defaultText.multiMapName.fr) {
    this._projet.pageCount++;
    this._ajouterPageCartesMultiplesDansVue(format, pageName); // la div de la map doit exister avant d'instancier la carte openlayer dans le model
    this._projet.addMultiMapPageToModel(pageName);
    Evenements.doitEnregistrer.notify();
  }

  /**
   * @param { MultiMapPageFormat } format
   * @param { string } pageName - Nom de la page (carte)
   * @param { number } jsonMapPageId - Optionel
   */
  _ajouterPageCartesMultiplesDansVue (format, pageName, jsonMapPageId) {
    const mapPageId = jsonMapPageId || this._projet.pageCount;
    MultiMapPageController.créerHtml(format, mapPageId);
    this._controleurPageCartesMultiples.updateSelectedOptions();
    if (OPTION_ONGLETS_CARTES) this._ajouterOngletPageCarteDansVue(pageName, mapPageId);
  }

  _écouterAjoutPageCartesMultiples () {
    const _this = this;
    this._projet.écouterMultiMapPageAjoutée(function (multiMapPage) {
      _this._controleurPageCartesMultiples.multiMapPage = multiMapPage;
      _this._controleurPageCartesMultiples.registerNewMultiMapListeners();
    });
  }

  /*
   * Formulaire renommer page carte
   */

  _renommerPageCarte () {
    const pageId = $(ContextMenu.MAP_TABS).data('pageId');
    const newName = $(MapRenameForm.INPUT).val();
    if (newName.length > 0) {
      const tab = '.tab__map-' + pageId;
      $(tab).text(newName);
      this._projet.getMapPage(pageId).title = newName;
      actualiserTexteEntete(this._projet);
      cacherFormulaireRenommerPageCarte();
      Evenements.doitEnregistrer.notify();
    } else {
      $(MapRenameForm.INPUT).effect('shake', 500);
    }
  }

  _renommerPageCarteSurToucheEntrer (event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      this._renommerPageCarte();
    }
  }

  /*
   * EXPORTATION PROJET
   */

  exportToJsonFile () {
    const jsonData = this._projet.toJSON();

    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return;
          }
          seen.add(value);
        }
        return value;
      };
    };

    const dataStr = JSON.stringify(jsonData, getCircularReplacer());
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = this._projet.title + '.ibalsac';

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  /** @param { boolean } autoSave */
  async exporterProjetVersAPI (autoSave) {
    const _this = this;
    const projet = this._projet;
    const jsonData = this._projet.toJSON();
    const urlAPI = OPTION_URL_API_OUVRIR_PROJET + idChercheurActif;
    if (gisSettings.options.project.autoSave) {
      if (autoSave) {
        jsonData.autoSave = 1;
      } else {
        jsonData.autoSave = 2;
      }
    }

    console.log('ProjectController: jsonData API : %o', jsonData);

    const getCircularReplacer = () => {
      const seen = new WeakSet();
      return (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return;
          }
          seen.add(value);
        }
        return value;
      };
    };

    const dataStr = JSON.stringify(jsonData, getCircularReplacer());
    console.log('ProjectController: dataStr API : %o', dataStr);

    if (dataStr.length > 0 && dataStr !== 'undefined') {
      const response = await fetch(urlAPI, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8'
        },
        body: dataStr
      });

      if (!autoSave) {
        if (response.ok) {
          $(HTML_AVERTISSEMENT_MESSAGE).data('text', 'modaleAvertissement.sauvegardeOk');
          Evenements.texteAjoute.notify();
          $(HTML_AVERTISSEMENT).show();
          if (!projet.idProjet) {
            const json = await response.json();
            // const idProjet = json.rows[0].id_projet;
            const idProjet = json.id_projet;
            console.log(json); // ! 2023-01-26 adam: pas sur que ce soit bon, me dit: {id_projet: '0'}, mais dataStr API : '{"idProjet":"5rK5yJgnMGfAqP4y1pss-","title":"Test projet Adam", ... (console un peu plus haut)
            projet.idProjet = idProjet;
            Evenements.doitEnregistrer.subscribe(_this.exporterProjetVersAPI.bind(_this, true));
          }
        } else {
          $(HTML_AVERTISSEMENT_MESSAGE).data('text', 'modaleAvertissement.sauvegardeErreur');
          Evenements.texteAjoute.notify();
          $(HTML_AVERTISSEMENT_MESSAGE).text($(HTML_AVERTISSEMENT_MESSAGE).text() + response.statusText);
          $(HTML_AVERTISSEMENT).show();
        }
      }
    } else {
      $(HTML_AVERTISSEMENT_MESSAGE).data('text', 'modaleAvertissement.sauvegardeErreur');
      Evenements.texteAjoute.notify();
      $(HTML_AVERTISSEMENT).show();
    }
  }

  /*
   * OUVERTURE PROJET EXISTANT
   */

  /** @param { string } nomFichierLocalJSON */
  _chargerProjetDepuisFichierLocal (nomFichierLocalJSON) {
    const _this = this;
    const reader = new window.FileReader();
    reader.onerror = gererErreurOuvertureProjetLocal;
    reader.onabort = function (e) {
      window.alert('File read cancelled');
    };
    reader.onloadend = function (evt) {
      if (evt.target.readyState === window.FileReader.DONE) {
        const result = evt.target.result;
        const projectJSON = (isJson(result)) ? result : JSON.parse(result);
        _this._ouvrirProjetDepuisFichierJson(projectJSON);
        $(ProjectMenu.OPEN_PROJECT_FORM.SECTION).toggleClass('hidden');
      }
    };
    reader.readAsText(nomFichierLocalJSON);
  }

  // @todo avertissement projet json version antérieure à convertir
  /** @param { string } projetJSON */
  async _ouvrirProjetDepuisFichierJson (projetJSON) {
    if (projetJSON.gisVersion === this._gisVersion) {
      const projet = await this._convertirProjetJSONVersProjet(await projetJSON);

      enleverAncienProjetDansVue();
      this._projet = await projet;
      this._activerProjetDepuisJSON(projetJSON, await this._projet);
      /* enleverAncienProjetDansVue();
      this._assignerProjetDepuisJSON(projectJSON); */
    } else {
      // @todo à faire
      console.log('ProjectController: projet de version antérieure à convertir');
    }
  }

  /**
   * @param { string } projetJSON
   * @returns { Project }
   */
  async _convertirProjetJSONVersProjet (projetJSON) {
    const projetConverti = new Project({ jsonObject: projetJSON });

    return await projetConverti;
  }

  /**
   * @param { string } projetJSON
   * @deprecated - inutilisé - en attente de voir si on peut supprimer
   */
  async _assignerProjetDepuisJSON (projetJSON) {
    console.log('@deprecated - ProjectController: _assignerProjetDepuisJSON: %o', projetJSON);
    const projetConverti = new Project({ jsonObject: projetJSON });
    this._projet = await projetConverti;
    this._activerProjetDepuisJSON(projetJSON, await projetConverti);
  }

  /**
   * @param { string } projetJSON
   * @param { Projet } projet
   */
  _activerProjetDepuisJSON (projetJSON, projet) {
    this._inscrireAuditeursDépendantsDeProjet(projet);
    this._controleurPageCartesMultiples = new MultiMapPageController();
    this._écouterAjoutPageCartesMultiples();
    this._chargerPagesCarteUniqueDepuisJSON(projetJSON, projet);
    this._chargerPagesCartesMultiplesDepuisJSON(projetJSON);
    this._chargerProjetActifDepuisJSON(projetJSON);

    this._ecouterChangementPageCarteActive();
    Evenements.changementProjetActif.notify(this._projet);

    this._ecouterAjoutPageCarteUnique();
    this._ecouterSuppressionPageCarteUnique();
    this._controleurPageCartesMultiples.activer();
  }

  /**
   * @param { string } projetJSON
   * @param { Project } projet
   */
  _chargerPagesCarteUniqueDepuisJSON (projetJSON, projet) {
    const _this = this;
    const loadedProject = projet;
    const singleMapPages = [];
    projetJSON.mapPages.forEach(function (mapPage) {
      if (mapPage[1].pageType === PageType.SINGLEMAPPAGE) {
        loadedProject.pageCount++;
        _this._ajouterPageCarteUniqueDansVue(mapPage[1].title, mapPage[1].id);
        loadedProject.addSingleMapPageToModelFromJson(mapPage[1]);
        singleMapPages.push(loadedProject.getMapPage(mapPage[1].id));
      }
    });
    this._controleurPageCartesMultiples.singleMapPages = singleMapPages;
  }

  /** @param { string } projetJSON */
  _chargerPagesCartesMultiplesDepuisJSON (projetJSON) {
    const _this = this;
    const loadedProject = this._projet;
    projetJSON.mapPages.forEach(function (mapPageJSON) {
      if (mapPageJSON[1].pageType === PageType.MULTIMAPPAGE) {
        loadedProject.pageCount++;
        _this._ajouterPageCartesMultiplesDansVue(mapPageJSON[1].format, mapPageJSON[1].title, mapPageJSON[1].id);
        loadedProject.addMultiMapPageToModelFromJson(mapPageJSON[1]);
      }
    });
  }

  /** @param { string } projetJSON */
  _chargerProjetActifDepuisJSON (projetJSON) {
    this._projet.setActiveMapPage(projetJSON.activeMapPageId);
    this._projet.activeMapPage.pageType === PageType.SINGLEMAPPAGE
      ? this._controleurPageCarteUnique.assignerPageCarteUniqueDepuisJSON(this._projet.activeMapPage)
      : activerPageCartesMultiples(this._projet.activeMapPage, this._controleurPageCartesMultiples);

    afficherPageCarte(this._projet.activeMapPage);
    actualiserTexteEntete(this._projet);
    écouterMenuContextuelEntête();
  }
}

/*
 * FONCTIONS
 */

/*
 * Entête
 */

/** @param { Project } projet */
function actualiserTexteEntete (projet) {
  if (OPTION_BARRE_TITRE) {
    $(GisSection.HEADER.PROJECT_NAME).text(projet.title);
    $(GisSection.HEADER.MAP_NAME).text(projet.activeMapPage.title);
  }
}

/** */
function écouterMenuContextuelEntête () {
  if (OPTION_BARRE_TITRE) {
    const menu = document.querySelector(ContextMenu.HEADER.SECTION);

    const setPosition = ({
      top,
      left
    }) => {
      hideContextMenus();
      menu.style.left = `${left}px`;
      menu.style.top = `${top}px`;
      $(ContextMenu.HEADER.SECTION).removeClass('hidden');
    };

    $(GisSection.HEADER.SECTION).on('contextmenu', e => {
      e.preventDefault();
      const origin = {
        left: e.pageX,
        top: e.pageY
      };
      setPosition(origin);
      return false;
    });
  }
}

/*
 * Avertissement
 */

/** */
function écouterModaleAvertissement () {
  $(HTML_AVERTISSEMENT_FERMER).off('click tap').on('click tap', () => $(HTML_AVERTISSEMENT).hide());
}

/*
 * Gestion page carte
 */

/**
 * @param { MultiMapPage } multiMapPage
 * @param { MultiMapPageController } multiMapPageController
 */
function activerPageCartesMultiples (multiMapPage, multiMapPageController) {
  multiMapPageController.multiMapPage = multiMapPage;
  $(HTML_MENUGAUCHE_BTNS_MULTIMAP).show();
  $(HTML_MENUGAUCHE_BTNS_SINGLEMAP).hide();
  $('.top-menu__button--singlemap').hide();
  $('.project-menu__option--singlemap').hide();
  $(GisSection.DATA_TABS).hide();
  LeftMenuController.afficherDétailsPageCartesMultiples();
}

/** @param { Page } page - SingleMapPage ou MultiMapPage */
function afficherPageCarte (page) {
  const mapDiv = '.map__section-' + page.id;
  const tabDiv = '.tab__map-' + page.id;
  Evenements.pageCarteAffichee.notify();
  $(GisSection.MAP).children().hide();
  $(mapDiv).show();
  $(GisSection.MAP_TABS.TAB).removeClass('active');
  $(tabDiv).addClass('active');
}

/** @param { Project } projet */
function supprimerPageCarteDeProjet (projet) {
  const pageId = $(ContextMenu.MAP_TABS).data('pageId');
  supprimerPageCarteDansVue(pageId);
  projet.removeMapPage(pageId);
}

/** @param { number } pageId */
function supprimerPageCarteDansVue (pageId) {
  const mapSection = '.map__section-' + pageId;
  const mapTab = '.tab__map-' + pageId;
  const dataSection = '#data__section-' + pageId;
  const dataTabs = '#data-tabs-' + pageId;

  $(mapSection).remove();
  $(mapTab).remove();
  $(dataSection).remove();
  $(dataTabs).remove();
}

/** */
function afficherFormulaireRenommerPageCarte () {
  $(MapRenameForm.FORM).removeClass('hidden');
  $(MapRenameForm.INPUT).focus();
}

/** */
function cacherFormulaireRenommerPageCarte () {
  $(MapRenameForm.FORM).addClass('hidden');
  $(MapRenameForm.INPUT).val('');
}

/** @param { number } pageId */
function écouterMenuContextuelOngletPageCarte (pageId) {
  const menu = document.querySelector(ContextMenu.MAP_TABS);
  const tabId = 'tab__map-' + pageId;

  const setPosition = ({ top, left }) => {
    hideContextMenus();
    const top_ = top - 130;
    menu.style.left = `${left}px`;
    menu.style.top = `${top_}px`;
    $(ContextMenu.MAP_TABS).data('pageId', pageId);
    $(ContextMenu.MAP_TABS).removeClass('hidden');
  };

  document.getElementById(tabId).addEventListener('contextmenu', e => {
    e.preventDefault();
    const origin = {
      left: e.pageX,
      top: e.pageY
    };
    setPosition(origin);
    return false;
  });
}

/*
 * Gestion Projet
 */

/** */
function enleverAncienProjetDansVue () {
  $(GisSection.MAP).empty();
  $(GisSection.MAP_TABS.SECTION).empty();
  $(LayoutSection.CONTENEUR).empty();
  $(GisSection.MAP_LAYERS_CONTAINER).empty();
}

/** */
function écouterDeclencheursAfficherRenommerProjet () {
  $(GisSection.HEADER.SECTION).off('dblclick').on('dblclick', () => afficherFormulaireRenommerProjet());
}

/** */
function afficherFormulaireRenommerProjet () {
  $(ProjectMenu.RENAME_FORM.SECTION).removeClass('hidden');
  $(ProjectMenu.RENAME_FORM.INPUT).focus();
  $(ProjectMenu.RENAME_FORM.INPUT).val($(GisSection.HEADER.PROJECT_NAME).text());
}

/** */
function cacherFormulaireRenommerProjet () {
  $(ProjectMenu.RENAME_FORM.SECTION).addClass('hidden');
  $(ProjectMenu.RENAME_FORM.INPUT).val('');
}

/*
 * Formulaire Projet espace chercheur
 */

/** */
function chargerFormulaireOuvrirProjetEspaceChercheur () {
  changerVisibiliteFormulaireOuvrirProjetEspaceChercheur();
  créerListeHTMLProjetsEspaceChercheur();
}

/** */
function changerVisibiliteFormulaireOuvrirProjetEspaceChercheur () {
  $(FormulaireOuvrirProjetChercheur.SECTION).toggleClass('hidden');
  $(FormulaireOuvrirProjetChercheur.TEXTE_AUCUN_PROJET).show();
  $(FormulaireOuvrirProjetChercheur.LISTE_PROJETS).html('');
}

/** */
async function créerListeHTMLProjetsEspaceChercheur () {
  const listeJSONFichiersEspaceChercheur = await obtenirListeProjetsEspaceChercheur();
  let listeHTML = '';
  if (listeJSONFichiersEspaceChercheur) {
    if (listeJSONFichiersEspaceChercheur.length === 0) {
      listeHTML = '<li class="ouvrir-projet-chercheur-form__aucun-projet" data-text="placeholder.noFile"></li>';
    } else {
      listeJSONFichiersEspaceChercheur.forEach(function (projet) {
        listeHTML += '<li class="liste-projets-chercheur__item" data-id-projet="' + projet.id + '">' +
        '<a href="' + URL_BASE + '?projet=' + projet.id + '&save"><span class="checkmark"></span>' + projet.titre;

        if (projet.date) {
          listeHTML += '<span class="date"> - ' + projet.date + '</span>';
        }
        if (projet.time) {
          listeHTML += '<span class="time">, ' + projet.time + '</span>';
        }
        listeHTML += '</a></li>';
      });
    }
    $(FormulaireOuvrirProjetChercheur.LISTE_PROJETS).append(listeHTML);
  } else {
    ajouterBoutonRéessayerChargerFormulaireProjetsEspaceChercheur();
  }
}

/** @returns { JSON } */
async function obtenirListeProjetsEspaceChercheur () {
  try {
    console.log('idChercheurActif = ' + idChercheurActif);
    console.log('url projets = ' + OPTION_URL_API_PROJETS + idChercheurActif);
    const response = await fetch(OPTION_URL_API_PROJETS + idChercheurActif, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    });
    // console.log('gisSettings.url.api.listeProjetsEspaceChercheur = ' + OPTION_URL_API_PROJETS);
    if (response.status >= 400) {
      console.log('Erreur serveur, response.status: ' + response.status);
      return false;
    }

    const jsonR = await response.json();
    console.log(jsonR);
    return jsonR;
  } catch (e) {
    console.log(e);
  }
}

/** @param { string } elementProjetJq */
/* function selectionnerProjetEspaceChercheur (elementProjetJq) {
  $('.liste-projets-chercheur__item input').prop('checked', false);
  $(elementProjetJq).parent().children('input').prop('checked', true);
} */

/**
 * @param { number } idProjet
 * @param { boolean } autoSave
 * @returns { JSON }
 */
async function obtenirProjetJsonEspaceChercheur (idProjet, autoSave) {
  // const _autoSave = autoSave === 1 ? '/autosave' : '';
  const _save = autoSave !== 1 ? '/save' : '';
  const urlapi = OPTION_URL_API_OUVRIR_PROJET + idProjet + _save;
  console.log('ProjectController: urlapi: ' + urlapi);
  try {
    const response = await fetch(urlapi, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json;charset=utf-8'
      }
    });
    if (response.status >= 400) {
      const messageErreur = 'Erreur serveur, response.status: ' + response.status;
      console.log(messageErreur);
      throw new Error(messageErreur);
    }
    const jsonData = await response.json();
    console.log('jsonData response : %o', jsonData[0]);
    return jsonData[0];
  } catch (error) {
    $(HTML_AVERTISSEMENT_MESSAGE).data('text', 'modaleAvertissement.erreur');
    console.log(error);
    Evenements.texteAjoute.notify();
    $(HTML_AVERTISSEMENT).show();
  }
}

/** */
function ajouterBoutonRéessayerChargerFormulaireProjetsEspaceChercheur () {
  $(FormulaireOuvrirProjetChercheur.LISTE_PROJETS).empty();
  $(FormulaireOuvrirProjetChercheur.LISTE_PROJETS).append('<button class="liste-projets-chercheur__refresh-button" data-text="button.retry"></button>');
  Evenements.texteAjoute.notify();
  $('.liste-projets-chercheur__refresh-button').on('click tap', function () {
    $('.liste-projets-chercheur__refresh-button').off('click tap');
    créerListeHTMLProjetsEspaceChercheur();
  });
}

/*
 * Formulaire Projet Local - NON-UTILISE DANS iBALSAC
 */

/** */
function fermerFormulaireOuvrirProjetLocal () {
  $(ProjectMenu.OPEN_PROJECT_FORM.NO_PROJECT).show();
  $(ProjectMenu.OPEN_PROJECT_FORM.FILE_NAME).hide().text('');
  $(ProjectMenu.OPEN_PROJECT_FORM.OPEN).off('click tap').on('click tap', () => $(ProjectMenu.OPEN_PROJECT_FORM.NO_PROJECT_TEXT).effect('shake'));
  $(ProjectMenu.OPEN_PROJECT_FORM.SECTION).toggleClass('hidden');
}

/** @param { event } evt */
function gererErreurOuvertureProjetLocal (evt) {
  switch (evt.target.error.code) {
    case evt.target.error.NOT_FOUND_ERR:
      window.alert('File Not Found!');
      break;
    case evt.target.error.NOT_READABLE_ERR:
      window.alert('File is not readable');
      break;
    case evt.target.error.ABORT_ERR:
      break;
    default:
      window.alert('An error occurred reading this file.');
  }
}
